/**
 * DiagramViewer — full-screen zoom/pan viewer for mermaid diagrams.
 *
 * Uses a CSS-only fixed overlay instead of the DS Dialog because
 * the DS Dialog (radix-ui monorepo) has a controlled-mode bug where
 * it doesn't open without a DialogTrigger child. Same issue as Sheet.
 * taiga-app works because it uses @radix-ui/react-dialog (old package).
 *
 * Features:
 * - Zoom +/- buttons with "Fit" reset
 * - Click-drag to pan
 * - Pinch-to-zoom on mobile, scroll wheel zoom on desktop
 * - Transient "Scroll to zoom · Drag to pan" hint (fades after 3s)
 * - Floating controls in bottom-right corner
 * - Escape to close, body scroll lock
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Button, cn } from "@taiga-platform/ui";
import DOMPurify from "dompurify";
import { Maximize2, X, ZoomIn, ZoomOut } from "lucide-react";

interface DiagramViewerProps {
  title: string;
  description?: string;
  svgHtml: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const FIT_ZOOM = 1;

export function DiagramViewer({
  title,
  description,
  svgHtml,
  open,
  onOpenChange,
}: DiagramViewerProps) {
  const [zoom, setZoom] = useState(FIT_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const lastPinchDistance = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setZoom(FIT_ZOOM);
      setPan({ x: 0, y: 0 });
      setShowHint(true);
      document.body.style.overflow = "hidden";
      const timer = setTimeout(() => setShowHint(false), 3000);
      return () => {
        clearTimeout(timer);
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const hideHint = useCallback(() => setShowHint(false), []);

  const handleZoomIn = () =>
    setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => {
    const nz = Math.max(zoom - ZOOM_STEP, MIN_ZOOM);
    setZoom(nz);
    if (nz <= FIT_ZOOM) setPan({ x: 0, y: 0 });
  };
  const handleFit = () => {
    setZoom(FIT_ZOOM);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      hideHint();
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
      e.preventDefault();
    },
    [pan, hideHint],
  );
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setPan({
        x: dragStart.current.panX + (e.clientX - dragStart.current.x),
        y: dragStart.current.panY + (e.clientY - dragStart.current.y),
      });
    },
    [isDragging],
  );
  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDistance.current = Math.hypot(dx, dy);
        setIsDragging(false);
      } else if (e.touches.length === 1) {
        setIsDragging(true);
        dragStart.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          panX: pan.x,
          panY: pan.y,
        };
      }
    },
    [pan],
  );
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && lastPinchDistance.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.hypot(dx, dy);
        const scale = distance / lastPinchDistance.current;
        setZoom((z) => {
          const nz = Math.min(Math.max(z * scale, MIN_ZOOM), MAX_ZOOM);
          if (nz <= FIT_ZOOM) setPan({ x: 0, y: 0 });
          return nz;
        });
        lastPinchDistance.current = distance;
        e.preventDefault();
      } else if (isDragging && e.touches.length === 1) {
        setPan({
          x:
            dragStart.current.panX +
            (e.touches[0].clientX - dragStart.current.x),
          y:
            dragStart.current.panY +
            (e.touches[0].clientY - dragStart.current.y),
        });
      }
    },
    [isDragging],
  );
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    lastPinchDistance.current = null;
  }, []);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      hideHint();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((z) => {
        const nz = Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
        if (nz <= FIT_ZOOM) setPan({ x: 0, y: 0 });
        return nz;
      });
    },
    [hideHint],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !open) return;
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [handleWheel, open]);

  const sanitizedSvg = useMemo(
    () =>
      DOMPurify.sanitize(svgHtml, {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_TAGS: ["foreignObject"],
      }),
    [svgHtml],
  );

  if (!open) return null;

  const zoomPercent = Math.round(zoom * 100);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-background"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex-1 min-w-0 pr-4">
          <h2 className="text-sm font-semibold text-foreground truncate">
            {title}
          </h2>
          {description && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          className="h-8 w-8 p-0 flex-shrink-0"
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </Button>
      </div>

      {/* Diagram canvas */}
      <div
        ref={containerRef}
        className={cn(
          "flex-1 bg-background overflow-hidden relative select-none cursor-grab",
          isDragging && "cursor-grabbing",
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "center center",
          }}
        >
          <div
            className="[&_svg]:max-w-full [&_svg]:h-auto"
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
          />
        </div>

        {/* Transient hint */}
        <div
          className={cn(
            "absolute top-4 left-1/2 -translate-x-1/2 px-3 py-2 bg-background/90 backdrop-blur-sm rounded-lg border shadow-sm text-xs text-muted-foreground transition-opacity duration-500",
            showHint
              ? "opacity-100"
              : "opacity-0 pointer-events-none",
          )}
        >
          Scroll to zoom · Drag to pan
        </div>

        {/* Floating zoom controls */}
        <div className="absolute bottom-4 right-4 flex items-center gap-1 bg-background/90 backdrop-blur-sm rounded-lg border shadow-sm p-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="h-8 w-8 p-0"
          >
            <ZoomOut className="size-4" />
            <span className="sr-only">Zoom out</span>
          </Button>
          <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
            {zoomPercent}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="h-8 w-8 p-0"
          >
            <ZoomIn className="size-4" />
            <span className="sr-only">Zoom in</span>
          </Button>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFit}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="size-4" />
            <span className="sr-only">Fit to screen</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
